import { useCallback, useEffect, useMemo, useState, type CSSProperties, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import {
  LOCATION_KIND_BEHAVIORS,
  LOCATION_KIND_CODE_PATTERN,
  locationKindsApi,
  type LocationKindBehavior,
  type LocationKindIcon as IconName,
  type LocationKindRequest,
  type LocationKindView,
} from "../../api/locations";
import type { ApiError } from "../../api/client";
import { detailsMap, formatApiError } from "../../lib/apiError";
import { useLocationKinds } from "../../lib/useLocationKinds";
import { useAuth } from "../../auth/AuthContext";
import { useToast } from "../../components/Toast";
import { useConfirm } from "../../components/ConfirmDialog";
import { Modal } from "../../components/Modal";
import { Select, type SelectOption } from "../../components/Select";
import Ic from "../../components/icons.jsx";
import { LOCATION_KIND_ICONS, LocationKindIcon } from "../../components/LocationKindIcon";

type Translate = (key: string, fallback: string, opts?: Record<string, unknown>) => string;

type FormMode = { kind: "create" } | { kind: "edit"; row: LocationKindView };

/** Serbian labels; rendered via t("locationKinds.behavior.<BEHAVIOR>", fallback). */
const BEHAVIOR_LABEL: Record<LocationKindBehavior, string> = {
  NONE: "Obična lokacija",
  EXTERNAL_SERVICE: "Spoljni servis",
  WORKSHOP: "Radionica",
};

export function behaviorLabel(t: Translate, b: LocationKindBehavior): string {
  return t(`locationKinds.behavior.${b}`, BEHAVIOR_LABEL[b] ?? b);
}

/**
 * Admin catalogue of location kinds (sprint 5a). The table reads the shared
 * `useLocationKinds` cache; every write refreshes it so the location form's
 * picker (possibly open in another tab of the SPA) sees the change.
 */
export default function AdminLocationKindsPage() {
  const { t } = useTranslation();
  const toast = useToast();
  const confirm = useConfirm();
  const { hasPermission } = useAuth();
  // Every codebook has its own functions (V26): location-kinds:create / edit / delete.
  const canCreate = hasPermission("location-kinds:create");
  const canEdit = hasPermission("location-kinds:edit");
  const canDelete = hasPermission("location-kinds:delete");
  const hasActions = canEdit || canDelete;
  const { kinds, loading, error, refresh } = useLocationKinds();
  const [form, setForm] = useState<FormMode | null>(null);

  async function handleDelete(row: LocationKindView) {
    const ok = await confirm({
      title: t("locationKinds.confirmDelete.title", "Brisanje vrste lokacije"),
      message: t("locationKinds.confirmDelete.message", "Obrisati vrstu „{{name}}“ ({{code}})?", { name: row.name, code: row.code }),
      confirmLabel: t("action.delete", "Obriši"),
      danger: true,
    });
    if (!ok) return;
    try {
      await locationKindsApi.remove(row.code);
      toast.success(t("locationKinds.toast.deleted", "Vrsta lokacije obrisana."));
      await refresh();
    } catch (err) {
      const e = err as ApiError;
      if (e.code === "IN_USE") {
        const count = Number(detailsMap(e)?.locationCount ?? row.locationCount);
        toast.error(t("locationKinds.error.inUse", "Vrsta se koristi na {{count}} lokacija — prvo promenite vrstu tim lokacijama.", { count }));
      } else if (e.code === "SYSTEM_KIND") {
        toast.error(t("locationKinds.error.systemKind", "Sistemska vrsta se ne može obrisati."));
      } else {
        toast.error(formatApiError(e));
      }
    }
  }

  async function handleSaved(created: boolean) {
    setForm(null);
    toast.success(created ? t("locationKinds.toast.created", "Vrsta lokacije dodata.") : t("locationKinds.toast.saved", "Vrsta lokacije sačuvana."));
    await refresh();
  }

  return (
    <div>
      <div className="page-head">
        <div>
          <h1>{t("locationKinds.title", "Vrste lokacija")}</h1>
          <div className="sub">{t("locationKinds.subtitle", "Šifarnik vrsta lokacija — Lokacija, Objekat, Magacin… Ponašanje određuje šta aplikacija radi sa lokacijom te vrste.")}</div>
        </div>
        {canCreate && (
          <div className="page-actions">
            <button type="button" className="btn primary" onClick={() => setForm({ kind: "create" })}>
              <Ic.plus /> {t("locationKinds.new", "Nova vrsta")}
            </button>
          </div>
        )}
      </div>

      {error && <div style={errorBox} role="alert">{error}</div>}

      <section className="card">
        {loading ? (
          <div className="empty">{t("common.loading", "Učitavanje…")}</div>
        ) : kinds.length === 0 ? (
          <div className="empty">{t("locationKinds.empty", "Još nema vrsta lokacija.")}</div>
        ) : (
          <div className="tbl-wrap">
            <table className="tbl list-card">
              <thead>
                <tr>
                  <th>{t("locationKinds.field.icon", "Ikonica")}</th>
                  <th>{t("locationKinds.col.code", "Šifra")}</th>
                  <th>{t("locationKinds.col.name", "Naziv")}</th>
                  <th>{t("locationKinds.col.behavior", "Ponašanje")}</th>
                  <th className="num">{t("locationKinds.col.sortOrder", "Redosled")}</th>
                  <th className="num">{t("locationKinds.col.locationCount", "Lokacija")}</th>
                  {hasActions && <th style={{ textAlign: "right" }}>{t("common.actions", "Radnje")}</th>}
                </tr>
              </thead>
              <tbody>
                {kinds.map((k) => (
                  <tr key={k.code}>
                    <td data-label={t("locationKinds.field.icon", "Ikonica")}>
                      <span title={t(`locationKinds.icon.${k.icon}`, LOCATION_KIND_ICONS[k.icon]?.label ?? "Oznaka lokacije")}>
                        <LocationKindIcon icon={k.icon} />
                      </span>
                    </td>
                    <td className="mono" data-label={t("locationKinds.col.code", "Šifra")}>{k.code}</td>
                    <td data-label={t("locationKinds.col.name", "Naziv")}>
                      <b>{k.name}</b>
                      {k.isSystem && (
                        <span className="tag info" style={{ marginLeft: 8 }} title={t("locationKinds.systemHint", "Sistemska vrsta: ne može se obrisati niti joj se menja ponašanje.")}>
                          {t("locationKinds.systemTag", "sistemska")}
                        </span>
                      )}
                    </td>
                    <td data-label={t("locationKinds.col.behavior", "Ponašanje")}>
                      {k.behavior === "NONE" ? <span className="muted">{behaviorLabel(t, k.behavior)}</span> : behaviorLabel(t, k.behavior)}
                    </td>
                    <td className="num" data-label={t("locationKinds.col.sortOrder", "Redosled")}>{k.sortOrder}</td>
                    <td className="num" data-label={t("locationKinds.col.locationCount", "Lokacija")}>{k.locationCount}</td>
                    {hasActions && (
                      <td style={{ textAlign: "right", whiteSpace: "nowrap" }} data-label={t("common.actions", "Radnje")}>
                        {canEdit && (
                          <button type="button" className="btn sm" onClick={() => setForm({ kind: "edit", row: k })}>
                            {t("action.edit", "Izmeni")}
                          </button>
                        )}
                        {canDelete && !k.isSystem && (
                          <button type="button" className="btn sm" style={{ ...dangerBtn, marginLeft: 6 }} onClick={() => handleDelete(k)}>
                            {t("action.delete", "Obriši")}
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {form && (
        <LocationKindFormModal mode={form} onClose={() => setForm(null)} onSaved={handleSaved} />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------

function LocationKindFormModal({ mode, onClose, onSaved }: {
  mode: FormMode;
  onClose: () => void;
  onSaved: (created: boolean) => Promise<void>;
}) {
  const { t } = useTranslation();
  const editing = mode.kind === "edit" ? mode.row : null;
  const [code, setCode] = useState(editing?.code ?? "");
  const [name, setName] = useState(editing?.name ?? "");
  const [icon, setIcon] = useState<IconName>(editing?.icon ?? "pin");
  const [behavior, setBehavior] = useState<LocationKindBehavior>(editing?.behavior ?? "NONE");
  const [sortOrder, setSortOrder] = useState(editing?.sortOrder?.toString() ?? "0");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const behaviorOptions = useMemo<SelectOption<LocationKindBehavior>[]>(
    () => LOCATION_KIND_BEHAVIORS.map((b) => ({ value: b, label: behaviorLabel(t, b) })),
    [t],
  );

  const onCodeChange = useCallback((raw: string) => {
    // Codes are upper-case identifiers; normalise as the admin types.
    setCode(raw.toUpperCase().replace(/[\s-]+/g, "_"));
  }, []);

  useEffect(() => {
    setError(null);
  }, [code, name, behavior, sortOrder, icon]);

  const codeValid = editing !== null || LOCATION_KIND_CODE_PATTERN.test(code);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!codeValid) {
      setError(t("locationKinds.error.code", "Šifra: veliko slovo pa 1–29 velikih slova, cifara ili donjih crta (npr. HALA, MAG_2)."));
      return;
    }
    const body: LocationKindRequest = {
      code: editing ? editing.code : code,
      name: name.trim(),
      icon,
      sortOrder: Number.parseInt(sortOrder, 10) || 0,
      // Behavior of a system kind is immutable — leave it out of the PUT (400 SYSTEM_KIND otherwise).
      ...(editing?.isSystem ? {} : { behavior }),
    };
    setSubmitting(true);
    try {
      if (editing) await locationKindsApi.update(editing.code, body);
      else await locationKindsApi.create(body);
      await onSaved(editing === null);
    } catch (err) {
      const ae = err as ApiError;
      if (ae.code === "CODE_TAKEN") setError(t("locationKinds.error.codeTaken", "Šifra je već zauzeta."));
      else if (ae.code === "SYSTEM_KIND") setError(t("locationKinds.error.systemBehavior", "Sistemskoj vrsti se ne može menjati ponašanje."));
      else setError(formatApiError(ae));
    } finally {
      setSubmitting(false);
    }
  }

  const behaviorLocked = editing?.isSystem === true;

  return (
    <Modal
      title={editing ? t("locationKinds.modal.editTitle", "Izmena vrste lokacije") : t("locationKinds.modal.createTitle", "Nova vrsta lokacije")}
      subtitle={editing ? <span style={{ fontFamily: "var(--mono)" }}>{editing.code}</span> : undefined}
      onClose={onClose}
    >
      <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {!editing && (
          <label className="field">
            <span>{t("locationKinds.field.code", "Šifra")} <span className="req-mark">*</span></span>
            <input
              className="input"
              value={code}
              onChange={(e) => onCodeChange(e.target.value)}
              required
              maxLength={30}
              autoComplete="off"
              spellCheck={false}
              placeholder="HALA"
              style={{ fontFamily: "var(--mono)", textTransform: "uppercase" }}
              aria-invalid={code.length > 0 && !codeValid}
            />
            <span className="muted" style={{ fontSize: 12 }}>
              {t("locationKinds.field.codeHint", "Velika slova, cifre i donja crta; počinje slovom; 2–30 znakova. Ne menja se posle kreiranja.")}
            </span>
          </label>
        )}
        <label className="field">
          <span>{t("locationKinds.field.name", "Naziv")} <span className="req-mark">*</span></span>
          <input className="input" value={name} onChange={(e) => setName(e.target.value)} required maxLength={100} />
        </label>
        <fieldset className="location-icon-field" disabled={submitting} aria-describedby="location-icon-hint">
          <legend>{t("locationKinds.field.icon", "Ikonica")}</legend>
          <div className="location-icon-grid">
            {(Object.keys(LOCATION_KIND_ICONS) as IconName[]).map((value) => (
              <label key={value} className="location-icon-option" title={t(`locationKinds.icon.${value}`, LOCATION_KIND_ICONS[value].label)}>
                <input type="radio" name="location-kind-icon" value={value} checked={icon === value} onChange={() => setIcon(value)} aria-label={t(`locationKinds.icon.${value}`, LOCATION_KIND_ICONS[value].label)} />
                <span className="location-icon-choice">
                  <LocationKindIcon icon={value} />
                </span>
              </label>
            ))}
          </div>
          <p id="location-icon-hint" className="location-icon-hint">
            {t("locationKinds.field.iconHint", "Prikazuje se uz sve lokacije ove vrste u stablu i kartonu lokacije.")}
          </p>
        </fieldset>
        <div className="form-grid">
          <label className="field" title={behaviorLocked ? t("locationKinds.systemHint", "Sistemska vrsta: ne može se obrisati niti joj se menja ponašanje.") : undefined}>
            <span>{t("locationKinds.field.behavior", "Ponašanje")}</span>
            <Select<LocationKindBehavior>
              value={behavior}
              options={behaviorOptions}
              onChange={setBehavior}
              disabled={behaviorLocked}
              ariaLabel={t("locationKinds.field.behavior", "Ponašanje")}
            />
            {behaviorLocked && (
              <span className="muted" style={{ fontSize: 12 }}>{t("locationKinds.behaviorLocked", "Sistemska vrsta — ponašanje je fiksno.")}</span>
            )}
          </label>
          <label className="field">
            <span>{t("locationKinds.field.sortOrder", "Redosled")}</span>
            <input className="input" type="number" value={sortOrder} onChange={(e) => setSortOrder(e.target.value)} inputMode="numeric" />
          </label>
        </div>
        <div className="muted" style={{ fontSize: 12 }}>
          {t("locationKinds.behaviorHint", "Spoljni servis: demontaža na tu lokaciju traži očekivani povratak i stavlja asset „na servis“. Radionica: interna radionica pogona.")}
        </div>
        {error && <div style={errorBox} role="alert">{error}</div>}
        <div className="form-actions">
          <button type="button" className="btn" onClick={onClose}>{t("action.cancel", "Otkaži")}</button>
          <button type="submit" className="btn primary" disabled={submitting}>
            {submitting ? t("action.saving", "Čuvanje…") : t("action.save", "Sačuvaj")}
          </button>
        </div>
      </form>
    </Modal>
  );
}

const errorBox: CSSProperties = { padding: 10, background: "rgba(182,53,58,.08)", color: "var(--err)", borderRadius: 6, fontSize: 13 };
const dangerBtn: CSSProperties = { color: "var(--err)", borderColor: "rgba(182,53,58,.35)" };
